import React, {ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState} from "react";
import {ProductListFilterItemRange, ProductListFilterItemRangeValue} from "@connox/typescript-library";
import {useProductListContext} from "../productListProvider/ProductListProvider";
import Slider from "rc-slider";
import {useTranslation} from "react-i18next";

// rc-slider handles classnames internally, so css modules won't work here
import {Button, ButtonType} from "../../lib/form/Button/Button";
import {useMediaQuery} from "react-responsive";
import {currencyFormat, unitFormat} from "../../../helpers/intlFormatters";
import RangeTitle from "../../atoms/RangeTitle";

type ProductFilterRangeProps = {
  item: ProductListFilterItemRange;
  onSelect: (value: ProductListFilterItemRangeValue) => void;
};

const Range = Slider.createSliderWithTooltip(Slider.Range);

const ProductFilterRange: React.FC<ProductFilterRangeProps> = ({item, onSelect}) => {
  const {t} = useTranslation();
  const {state, dispatch} = useProductListContext();
  const [value, setValue] = useState<ProductListFilterItemRangeValue>(item.value);
  const isMobile = useMediaQuery({query: "(max-width: 940px)"});
  const [limit, setLimit] = useState<ProductListFilterItemRangeValue>({min: 0, max: 0});
  const formatValue = useMemo(() => (item.field === "price" ? currencyFormat : unitFormat), []);
  const [inputValues, setInputValues] = useState<{min: string; max: string}>({
    min: formatValue(item.value.min),
    max: formatValue(item.value.max),
  });
  const [focused, setFocused] = useState<"min" | "max">();
  const [open, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initialValue = state.filters[item.field] as ProductListFilterItemRangeValue;
    if (initialValue) {
      setValue(initialValue);
      if (!focused) {
        setInputValues({
          min: formatValue(initialValue.min),
          max: formatValue(initialValue.max),
        });
      }
    } else {
      setValue(limit);
      if (!focused) {
        setInputValues({
          min: formatValue(limit.min),
          max: formatValue(limit.max),
        });
      }
    }
  }, [state.filters[item.field]]);

  useEffect(() => {
    const filterValue = state.filters[item.field] as ProductListFilterItemRangeValue;
    if (!filterValue) {
      setValue(limit);
      if (!focused) {
        setInputValues({
          min: formatValue(limit.min),
          max: formatValue(limit.max),
        });
      }
    } else {
      if (limit.max >= filterValue.max && limit.min <= filterValue.min) {
        setValue(filterValue);
        if (!focused) {
          setInputValues({
            min: formatValue(filterValue.min),
            max: formatValue(filterValue.max),
          });
        }
      }
      if (limit.min > filterValue.min) {
        setValue({...value, min: limit.min});
        setInputValues({...inputValues, min: formatValue(limit.min)});
      }
      if (limit.max < filterValue.max) {
        setValue({...value, max: limit.max});
        setInputValues({...inputValues, max: formatValue(limit.max)});
      }
      if (limit.min > filterValue.min && limit.max < filterValue.max) {
        dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
      }
    }
  }, [limit.min, limit.max]);

  useEffect(() => {
    const minMaxValue = state.rangeFilterMap?.get(item.field);
    if (minMaxValue) {
      setLimit({...minMaxValue});
    }
  }, [state.rangeFilterMap?.get(item.field)]);

  if (limit.min === limit.max) {
    return null;
  }

  // submit the (last) set value
  const onRangeChangeComplete = () => {
    submitRange(value);
  };

  // dispatch changes
  const submitRange = (submitRangeValue: ProductListFilterItemRangeValue) => {
    if (isMobile) {
      onSelect(value);

      return;
    }
    if (submitRangeValue.min === limit.min && submitRangeValue.max === limit.max) {
      dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
    } else {
      dispatch({type: "SET_FILTER", payload: {field: item.field, value: submitRangeValue}});
    }
  };

  // On the fly range change handler
  const onRangeChange = (val: [number, number]) => {
    setValue({min: val[0], max: val[1]});
    setInputValues({min: formatValue(val[0]), max: formatValue(val[1])});
  };

  const onBlurInputMin = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(undefined);
    const eventTargetValue = Number(event.target.value);

    // validation
    // if Math.floor(`min` input) smaller than boundary min
    if (eventTargetValue < limit.min) {
      setInputValues({min: formatValue(limit.min), max: formatValue(value.max)});
      setValue({min: limit.min, max: value.max});
      onSelect({min: limit.min, max: value.max});

      return;
    }

    // if `min` input set to boundary.max or above, or `min` input is
    // bigger than or equal to the former value.max (=== limit.max)
    if (eventTargetValue >= limit.max || (eventTargetValue >= value.max && value.max === limit.max)) {
      setInputValues({min: formatValue(value.min), max: formatValue(value.max)});
      onSelect(value);

      return;
    }

    // if `min` input is bigger or equal to former `max` value
    // and former `max` value is smaller than boundary.max
    if (eventTargetValue >= value.max && value.max < limit.max) {
      setInputValues({min: formatValue(eventTargetValue), max: formatValue(limit.max)});
      setValue({min: eventTargetValue, max: limit.max});
      onSelect({min: eventTargetValue, max: limit.max});

      return;
    }
    // end validation

    setInputValues({
      min: formatValue(Number(eventTargetValue)),
      max: formatValue(Number(inputValues.max)),
    });
    setValue({...value, min: eventTargetValue});
    onSelect({...value, min: eventTargetValue});
  };

  const onBlurInputMax = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(undefined);
    const eventTargetValue = Number(event.target.value);

    // validation
    // if Math.ceil(`max` input) bigger than boundary max
    if (eventTargetValue > limit.max) {
      setInputValues({min: formatValue(value.min), max: formatValue(limit.max)});
      setValue({min: value.min, max: limit.max});
      onSelect({min: value.min, max: limit.max});

      return;
    }

    // if `max` input set to boundary.min or below, or `max` input is
    // smaller than or equal to the former value.min (=== limit.min)
    if (eventTargetValue <= limit.min || (eventTargetValue <= value.min && value.min === limit.min)) {
      setInputValues({min: formatValue(value.min), max: formatValue(value.max)});
      onSelect(value);

      return;
    }

    // if `max` input is smaller or equal to former `max` value
    // and former `min` value is bigger than boundary.min
    if (eventTargetValue <= value.min && value.min > limit.min) {
      setInputValues({min: formatValue(limit.min), max: formatValue(eventTargetValue)});
      setValue({min: limit.min, max: eventTargetValue});
      onSelect({min: limit.min, max: eventTargetValue});

      return;
    }
    // end validation

    setInputValues({
      min: formatValue(Number(inputValues.min)),
      max: formatValue(Number(eventTargetValue)),
    });
    setValue({...value, max: eventTargetValue});
    onSelect({...value, max: eventTargetValue});
  };

  const onFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const name = event.target.name as "min" | "max";
    setFocused(name);
    setInputValues({
      min: inputValues.min.replace(/\D/g, ""),
      max: inputValues.max.replace(/\D/g, ""),
    });
  };

  // form onSubmit handler (button or enter key press)
  const submitData = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let max = Number(inputValues.max.replace(/\D/g, ""));
    let min = Number(inputValues.min.replace(/\D/g, ""));

    // validation
    if (min < limit.min) {
      setInputValues({...inputValues, min: String(limit.min)});
      min = limit.min;
    }

    if (min > limit.max || max < limit.min) {
      setInputValues({min: String(value.min), max: String(value.min)});
      min = value.min;
      max = value.max;
    } else if (min >= max) {
      if (focused === "min") {
        if (max === limit.max) {
          setInputValues({...inputValues, min: String(value.min)});
          min = value.min;
        } else {
          setInputValues({...inputValues, max: String(limit.max)});
          max = limit.max;
        }
      } else if (focused === "max") {
        if (min === limit.min) {
          setInputValues({...inputValues, max: String(value.max)});
          max = value.max;
        } else {
          setInputValues({...inputValues, min: String(limit.min)});
          min = limit.min;
        }
      }
    }

    if (max > limit.max) {
      setInputValues({...inputValues, max: String(limit.max)});
      max = limit.max;
    }

    // end validation

    const valueToSubmit = {min, max};
    setValue(valueToSubmit);
    submitRange(valueToSubmit);
  };

  // onChange handler on input fields
  const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name as "min" | "max";

    // (/\D/g) is RegExp detect anything but not numbers
    setInputValues({...inputValues, [name]: event.target.value.replace(/\D/g, "")});
  };

  return (
    <div className="filter-item filter-item__range" ref={containerRef}>
      <div
        onClick={() => {
          setOpen(!open);
        }}
        className="filter-item__title"
      >
        <span>
          <RangeTitle className={"filter-item__count"} item={item} />
        </span>
        <i className={`icon icon-size--16 icon-chevron-${open ? "up" : "down"}`} />
      </div>
      {open && (
        <div
          className="filter-item__content"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Range
            max={limit.max}
            min={limit.min}
            onChange={onRangeChange}
            tipFormatter={formatValue}
            pushable={1}
            onAfterChange={onRangeChangeComplete}
            value={[value.min, value.max]}
            allowCross={false}
          />
          <form onSubmit={submitData}>
            <div className="filter-item__range__input">
              <input
                type={"text"}
                name="min"
                value={inputValues.min}
                onBlur={onBlurInputMin}
                onFocus={onFocusInput}
                onChange={onChangeInput}
              />
              <span />
              <input
                type={"text"}
                name="max"
                value={inputValues.max}
                onBlur={onBlurInputMax}
                onFocus={onFocusInput}
                onChange={onChangeInput}
              />
            </div>

            {!isMobile && <Button type={ButtonType.outlined}>{t("general.apply")}</Button>}
          </form>
        </div>
      )}
    </div>
  );
};

export default ProductFilterRange;
